import useVuelidate from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import Draggable from 'vuedraggable';
import MenuCommonDialog from '@/components/Store/MenuCommonDialog/MenuCommonDialog.vue';
import { KEY_CODES } from '@/utils/constants';
import bus from '@/utils/bus';
import { required } from '@/utils/validators';

export default {
  name: 'MenuItemsEditorCategoryEntrees',
  components: {
    MenuCommonDialog,
    Draggable,
  },
  props: {
    menu: {
      required: true,
      type: Object,
    },
    category: {
      required: true,
      type: Object,
    },
    errorCategoryMessages: {
      default: () => ([]),
      type: Array,
    },
    options: {
      default: () => ({}),
      type: Object,
    },
    selectedEntreeIndex: {
      default: null,
      type: Number,
    },
    showRemoveButton: {
      default: false,
      type: Boolean,
    },
  },
  setup() {
    return { v$: useVuelidate({ $lazy: true }) };
  },
  data() {
    return {
      draggingKey: null,
      entrees: [],
      show: true,
      showRemoveCategoryDialog: false,
    };
  },
  computed: {
    hasCategoryErrors() {
      return !!this.errorCategoryMessages?.length;
    },
    hasEmptyEntrees() {
      return this.entrees?.some(({ name }) => !name);
    },
  },
  created() {
    this.entrees = this.category?.entrees || [];
  },
  methods: {
    addNewEntree(categoryIndex) {
      this.$emit('add-new-entree', categoryIndex);
    },
    isEntreeKeyInvalid(entreeIndex, key) {
      return !!this.v$?.entrees?.$each?.$response?.$data?.[entreeIndex]?.[key]?.$invalid;
    },
    onCategoryNameChanges($event, category) {
      this.$emit('input', $event);
      category.setName($event);
    },
    onCheckAndAddNewEntree($event, viewIndex, categoryIndex, key) {
      if (this.hasEmptyEntrees || $event?.isComposing) {
        return;
      }
      const isLastItem = this.entrees?.length === viewIndex + 1;
      if (isLastItem) {
        this.addNewEntree(categoryIndex);
      }
      this.onFocusNext($event, viewIndex, key);
    },
    onCloseRemoveCategoryDialog() {
      this.showRemoveCategoryDialog = false;
    },
    onFocus($event, key) {
      const {
        isComposing,
        keyCode,
        target: {
          selectionEnd,
          value,
        },
      } = $event;
      if (keyCode === KEY_CODES.LEFT && Number.isInteger(selectionEnd) && selectionEnd >= 0) {
        // eslint-disable-next-line no-param-reassign
        $event.target.selectionStart = selectionEnd - 1;
        // eslint-disable-next-line no-param-reassign
        $event.target.selectionEnd = selectionEnd - 1;
        return;
      }
      if (
        keyCode === KEY_CODES.RIGHT
        && Number.isInteger(selectionEnd)
        && selectionEnd < value.length
      ) {
        // eslint-disable-next-line no-param-reassign
        $event.target.selectionStart = selectionEnd + 1;
        // eslint-disable-next-line no-param-reassign
        $event.target.selectionEnd = selectionEnd + 1;
        return;
      }
      if (!key || isComposing) {
        return;
      }
      this.$nextTick(() => {
        this.$refs[key][0].focus();
      });
    },
    onFocusAndSetDisplayEntree($event, key, entreeIndex) {
      if (!key) {
        throw new TypeError('key is required to focus the input.');
      }
      this.onFocus($event, key);
      bus.$emit('SET_DISPLAY_ENTREE_WITH_ENTREE_INDEX', entreeIndex);
    },
    onFocusNext($event, viewIndex = 0, key) {
      const isLastItem = this.entrees?.length === viewIndex + 1;
      if (!key || isLastItem || $event?.isComposing) {
        return;
      }
      const index = this.entrees[viewIndex + 1]?.index;
      this.onFocusToIndex($event, key, index);
    },
    onFocusPre($event, viewIndex = 0, key) {
      const isFirstItem = viewIndex === 0;
      if (!key || isFirstItem || $event?.isComposing) {
        return;
      }
      const index = this.entrees[viewIndex - 1]?.index;
      this.onFocusToIndex($event, key, index);
    },
    onFocusToIndex($event, key, entreeIndex) {
      if (!key || !Number.isInteger(entreeIndex)) {
        return;
      }
      this.onFocusAndSetDisplayEntree($event, `${key}-${entreeIndex}`, entreeIndex);
    },
    onNameChanges($event, entree) {
      this.$emit('input', $event);
      entree.setName($event);
    },
    onPriceChanges($event, entree) {
      this.$emit('input', $event);
      entree.setPrice($event);
    },
    onRemoveEntree(entree) {
      if (!entree) {
        throw new TypeError('Entree is required for removing');
      }
      this.$emit('remove-entree', entree);
    },
    onCopyEntree(entree, index) {
      this.$emit('copy-entree', {
        id: entree.index,
        viewIndex: index,
        categoryIndex: this.category.index,
      });
    },
    onDragStart(event) {
      this.draggingKey = event.item.dataset.key;
    },
    onDragEnd() {
      this.draggingKey = null;
    },
    onDragChange() {
      const category = this.menu.getCategory(this.category.index);
      category.setEntreesViewIndex();
    },
  },
  validations() {
    return {
      entrees: {
        $each: helpers.forEach({
          _name: { required: required('品項名稱') },
          _price: { required: required('價格') },
        }),
      },
    };
  },
};
